import { FilteredItemProps } from '@delmonicos/shared-design-system-react/src/components/filtered-item/filtered-item';
import { GatewayGetTransactionsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/transactions/models/inputs/gateway-get-transactions-paginated.model';
import { TransactionModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/transaction/paginated/transaction.model';
import {
  ColumnDef,
  ColumnOrderState,
  VisibilityState,
  createColumnHelper,
} from '@tanstack/react-table';

import { ChargePointFilterStateController } from '@app/feature/charge-point/charge-point-filter-state/charge-point-filter-state.controller';
import { LocationFilterStateController } from '@app/feature/location/location-filter-state/location-filter-state.controller';

import { TransactionCreatedAfterFilterStateController } from '../transaction-created-after-filter-state/transaction-created-after-filter-state.controller';
import { TransactionCreatedBeforeFilterStateController } from '../transaction-created-before-filter-state/transaction-created-before-filter-state.controller';
import { TransactionStateFilterStateController } from '../transaction-state-filter-state/transaction-filter-state.controller';
import { TransactionListChargePointColumn } from './transaction-list-charge-point.column';
import { TransactionListDurationColumn } from './transaction-list-duration.column';
import { TransactionListKwhColumn } from './transaction-list-kwh.column';
import { TransactionListLocationColumn } from './transaction-list-location.column';
import { TransactionListOcpiSessionIdColumn } from './transaction-list-ocpi-session-id.column';
import { TransactionListStartedAtColumn } from './transaction-list-started-at.column';
import { TransactionListStateColumn } from './transaction-list-state.column';
import { TransactionListStoppedAtColumn } from './transaction-list-stopped-at.column';
import { TransactionListTokenAuthIdColumn } from './transaction-list-token-auth-id.column';
import { TransactionListTokenTypeColumn } from './transaction-list-token-type.column';
import { TransactionListTotalCostColumn } from './transaction-list-total-cost.column';

// /////////////////////////////////////////////////////////////////////////////
// SORTING
// /////////////////////////////////////////////////////////////////////////////

export const TRANSACTION_LIST_SORT_FIELD: NonNullable<
  GatewayGetTransactionsPaginatedModel['sortField']
> = 'createdAt';

export const TRANSACTION_LIST_SORT_ORDER: NonNullable<
  GatewayGetTransactionsPaginatedModel['sortOrder']
> = 1;

// /////////////////////////////////////////////////////////////////////////////
// FILTERING
// /////////////////////////////////////////////////////////////////////////////

type ResourceListFilter = Partial<
  Record<
    keyof GatewayGetTransactionsPaginatedModel,
    {
      componentType: React.ComponentType<{
        value: any;
        onRemove: FilteredItemProps['onRemove'];
      }>;
    }
  >
>;

export const TRANSACTION_LIST_FILTER: ResourceListFilter = {
  createdAfter: {
    componentType: TransactionCreatedAfterFilterStateController,
  },
  createdBefore: {
    componentType: TransactionCreatedBeforeFilterStateController,
  },
  locationId: {
    componentType: LocationFilterStateController,
  },
  chargePointId: {
    componentType: ChargePointFilterStateController,
  },
  currentState: {
    componentType: TransactionStateFilterStateController,
  },
} as const;

export const TRANSACTION_LIST_FILTER_NAME_LIST = Object.keys(
  TRANSACTION_LIST_FILTER,
) as Array<keyof typeof TRANSACTION_LIST_FILTER>;

// /////////////////////////////////////////////////////////////////////////////
// DISPLAYING
// /////////////////////////////////////////////////////////////////////////////

export const transactionListColumnHelper =
  createColumnHelper<TransactionModel>();

type ResourceColumnType = ColumnDef<TransactionModel, any>[];

export const TRANSACTION_LIST_COLUMN_LIST: ResourceColumnType = [
  transactionListColumnHelper.accessor(
    'ocpiSessionId',
    TransactionListOcpiSessionIdColumn,
  ),
  transactionListColumnHelper.accessor(
    'token.type',
    TransactionListTokenTypeColumn,
  ),
  transactionListColumnHelper.accessor(
    'token.authId',
    TransactionListTokenAuthIdColumn,
  ),
  transactionListColumnHelper.accessor(
    'chargePoint',
    TransactionListChargePointColumn,
  ),
  transactionListColumnHelper.accessor(
    'location',
    TransactionListLocationColumn,
  ),
  transactionListColumnHelper.accessor('state', TransactionListStateColumn),
  transactionListColumnHelper.accessor(
    'startedAt',
    TransactionListStartedAtColumn,
  ),
  transactionListColumnHelper.display(TransactionListDurationColumn),
  transactionListColumnHelper.accessor(
    'stoppedAt',
    TransactionListStoppedAtColumn,
  ),
  transactionListColumnHelper.accessor('kwh', TransactionListKwhColumn),
  transactionListColumnHelper.accessor(
    'totalCost',
    TransactionListTotalCostColumn,
  ),
];

export const TRANSACTION_LIST_COLUMN_ID_LIST =
  TRANSACTION_LIST_COLUMN_LIST.flatMap((columnDef) => {
    if ('accessorKey' in columnDef) {
      return [columnDef.id ?? (columnDef.accessorKey as string)];
    }

    if (columnDef.id) {
      return [columnDef.id];
    }

    return [];
  });

export const TRANSACTION_LIST_COLUMN_ORDER = [
  ...TRANSACTION_LIST_COLUMN_ID_LIST,
];

export const TRANSACTION_LIST_COLUMN_VISIBILITY: VisibilityState =
  TRANSACTION_LIST_COLUMN_ID_LIST.reduce((columnVisibility, columnId) => {
    return { ...columnVisibility, [columnId]: true };
  }, {} as VisibilityState);

// /////////////////////////////////////////////////////////////////////////////
// OPTIONS
// /////////////////////////////////////////////////////////////////////////////

export interface TransactionListOptionsModel {
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
}

export const TRANSACTION_LIST_DEFAULT_OPTIONS: TransactionListOptionsModel = {
  columnVisibility: TRANSACTION_LIST_COLUMN_VISIBILITY,
  columnOrder: TRANSACTION_LIST_COLUMN_ORDER,
};

export const TRANSACTION_LIST_COLUMN_LABEL_I18N_KEY: Record<string, string> = {
  ocpiSessionId: 'delmonicos-shared-sdk.outputs-transaction.ocpi-session-id',
  tokenType: 'delmonicos-shared-sdk.outputs-token.type',
  tokenAuthId: 'delmonicos-shared-sdk.outputs-token.auth-id',
  chargePoint: 'delmonicos-shared-sdk.outputs-transaction.charge-point',
  location: 'delmonicos-shared-sdk.outputs-transaction.location',
  state: 'delmonicos-shared-sdk.outputs-transaction.state',
  startedAt: 'delmonicos-shared-sdk.outputs-transaction.started-at',
  duration: 'delmonicos-shared-sdk.outputs-transaction.duration',
  stoppedAt: 'delmonicos-shared-sdk.outputs-transaction.stopped-at',
  kwh: 'delmonicos-shared-sdk.outputs-transaction.kwh',
  totalCost: 'delmonicos-shared-sdk.outputs-transaction.total-cost',
};
